import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import CountUp from "react-countup";
import ReactApexChart from "react-apexcharts";
import { all_routes } from "../../../router/all_routes";
import { useGetCourseOverviewQuery, useGetBookRequestsSummaryQuery, useGetChartsInfoQuery } from "../../../mainMenu/api/dashboardApi";

const CenterAnalytics = () => {
  const { id } = useParams<{ id: string }>();
  const centerId = id ? Number(id) : undefined;

  const [selectedBookCourseIdx, setSelectedBookCourseIdx] = useState(0);
  const [selectedBookLevel, setSelectedBookLevel] = useState("all");

  const { data: bookRequestsData, isLoading: bookRequestsLoading } = useGetBookRequestsSummaryQuery(
    { center_id: centerId },
    { skip: !centerId }
  );

  const bookCourses = bookRequestsData?.data?.book_requests ?? [];
  const selectedBookCourse = bookCourses[selectedBookCourseIdx];

  const filteredBookData = selectedBookCourse
    ? (selectedBookLevel === "all"
        ? selectedBookCourse.level_wise
        : selectedBookCourse.level_wise.filter(r => r.level === Number(selectedBookLevel)))
    : [];

  const totalRequested = filteredBookData.reduce((s, r) => s + r.requested, 0);
  const totalIssued = filteredBookData.reduce((s, r) => s + r.issued, 0);
  const totalPending = filteredBookData.reduce((s, r) => s + r.pending, 0);
  const totalRejected = filteredBookData.reduce((s, r) => s + r.rejected, 0);

  const [selectedOverviewCourseIdx, setSelectedOverviewCourseIdx] = useState(0);
  const [selectedOverviewLevel, setSelectedOverviewLevel] = useState("all");

  const { data: courseOverviewData, isLoading: courseOverviewLoading } = useGetCourseOverviewQuery(
    { center_id: centerId },
    { skip: !centerId }
  );

  const courses = courseOverviewData?.data?.course_overview ?? [];
  const selectedCourse = courses[selectedOverviewCourseIdx];

  const currentOverview = selectedCourse
    ? selectedOverviewLevel === "all"
      ? selectedCourse.overall
      : selectedCourse.level_wise.find(l => l.level === Number(selectedOverviewLevel)) ?? selectedCourse.overall
    : null;

  const centerName = courseOverviewData?.data?.center_name;

  const { data: chartsData } = useGetChartsInfoQuery(
    { center_id: centerId },
    { skip: !centerId }
  );
  const charts = chartsData?.data?.charts;

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Page Header */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Center Analytics{centerName ? ` - ${centerName}` : ""}</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={all_routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={all_routes.centerList}>Centers</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={all_routes.centerDetailsId.replace(":id", id || "")}>Center Details</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">Analytics</li>
              </ol>
            </nav>
          </div>
        </div>

        {/* Key Metrics - Course Overview */}
        <div className="mb-4">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h4 className="text-primary mb-0">Course Overview</h4>
            <div className="d-flex gap-2">
              <select
                className="form-select form-select-sm w-auto"
                value={selectedOverviewCourseIdx}
                onChange={(e) => { setSelectedOverviewCourseIdx(Number(e.target.value)); setSelectedOverviewLevel("all"); }}
              >
                {courses.map((c, i) => (
                  <option key={c.course_id} value={i}>{c.course_name}</option>
                ))}
              </select>
              <select
                className="form-select form-select-sm w-auto"
                value={selectedOverviewLevel}
                onChange={(e) => setSelectedOverviewLevel(e.target.value)}
              >
                <option value="all">All Levels</option>
                {selectedCourse && Array.from({ length: selectedCourse.levels }, (_, i) => (
                  <option key={i + 1} value={i + 1}>Level {i + 1}</option>
                ))}
              </select>
            </div>
          </div>
          {courseOverviewLoading ? (
            <div className="text-center py-3">Loading...</div>
          ) : currentOverview ? (
          <div className="row">
            <div className="col-xxl-4 col-md-4 col-sm-6 d-flex">
              <div className="card flex-fill border-0">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className={`avatar avatar-xl ${selectedOverviewCourseIdx === 0 ? "bg-primary-transparent" : "bg-danger-transparent"} me-2 p-1`}>
                      <i className={`ti ${selectedOverviewCourseIdx === 0 ? "ti-brain text-purple" : "ti-pencil text-orange"} fs-24`}></i>
                    </div>
                    <div className="overflow-hidden flex-fill">
                      <div className="d-flex align-items-center justify-content-between">
                        <h2 className="counter"><CountUp end={currentOverview.total_students} /></h2>
                      </div>
                      <p>Total Students</p>
                    </div>
                  </div>
                  <div className="d-flex justify-content-between mt-2 text-muted fs-13">
                    <span>Active: {currentOverview.active_students} ({currentOverview.active_percentage})</span>
                    <span>Inactive: {currentOverview.inactive_students}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xxl-4 col-md-4 col-sm-6 d-flex">
              <div className="card flex-fill border-0">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="avatar avatar-xl me-2 bg-info-transparent p-1">
                      <i className="ti ti-user-check fs-24 text-info"></i>
                    </div>
                    <div className="overflow-hidden flex-fill">
                      <div className="d-flex align-items-center justify-content-between">
                        <h2>{currentOverview.attendance_rate}</h2>
                      </div>
                      <p>Attendance Rate <span className="fs-12 text-muted">(Current Month)</span></p>
                    </div>
                  </div>
                  <div className="d-flex justify-content-between mt-2 text-muted fs-13">
                    <span>Present: {currentOverview.present}</span>
                    <span>Absent: {currentOverview.absent}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xxl-4 col-md-4 col-sm-6 d-flex">
              <div className="card flex-fill border-0">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="avatar avatar-xl me-2 bg-warning-transparent p-1">
                      <i className="ti ti-receipt fs-24 text-warning"></i>
                    </div>
                    <div className="overflow-hidden flex-fill">
                      <div className="d-flex align-items-center justify-content-between">
                        <h2>₹{currentOverview.collected_fee.toLocaleString("en-IN")}</h2>
                        <span className="badge bg-warning">{currentOverview.collected_percentage}</span>
                      </div>
                      <p>Fees Collection</p>
                    </div>
                  </div>
                  <div className="d-flex justify-content-between mt-2 text-muted fs-13">
                    <span>Collected: ₹{currentOverview.collected_fee.toLocaleString("en-IN")}</span>
                    <span>Pending: ₹{currentOverview.pending_fee.toLocaleString("en-IN")}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          ) : null}
        </div>

        {/* Book Requests - Course & Level Wise */}
        <div className="mb-4">
          <h4 className="mb-3 text-primary">Book Requests</h4>
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
                  <h5 className="mb-0">Level Wise Book Requests</h5>
                  <div className="d-flex gap-2">
                    <select
                      className="form-select form-select-sm w-auto"
                      value={selectedBookCourseIdx}
                      onChange={(e) => { setSelectedBookCourseIdx(Number(e.target.value)); setSelectedBookLevel("all"); }}
                    >
                      {bookCourses.map((c, i) => (
                        <option key={c.course_id} value={i}>{c.course_name}</option>
                      ))}
                    </select>
                    <select
                      className="form-select form-select-sm w-auto"
                      value={selectedBookLevel}
                      onChange={(e) => setSelectedBookLevel(e.target.value)}
                    >
                      <option value="all">All Levels</option>
                      {selectedBookCourse?.level_wise.map(r => (
                        <option key={r.level} value={r.level}>Level {r.level}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="card-body">
                  {bookRequestsLoading ? (
                    <div className="text-center py-3">Loading...</div>
                  ) : (
                  <div className="row row-cols-1 row-cols-sm-4 g-3">
                    <div className="col">
                      <div className="card bg-primary-transparent border-0 h-100">
                        <div className="card-body text-center">
                          <div className="avatar avatar-lg bg-primary rounded-circle mb-2 mx-auto">
                            <i className="ti ti-book text-white fs-20"></i>
                          </div>
                          <h2 className="mb-1"><CountUp end={totalRequested} /></h2>
                          <p className="mb-0 text-muted">Total Requested</p>
                        </div>
                      </div>
                    </div>
                    <div className="col">
                      <div className="card bg-success-transparent border-0 h-100">
                        <div className="card-body text-center">
                          <div className="avatar avatar-lg bg-success rounded-circle mb-2 mx-auto">
                            <i className="ti ti-book-upload text-white fs-20"></i>
                          </div>
                          <h2 className="mb-1"><CountUp end={totalIssued} /></h2>
                          <p className="mb-0 text-muted">Books Issued</p>
                        </div>
                      </div>
                    </div>
                    <div className="col">
                      <div className="card bg-warning-transparent border-0 h-100">
                        <div className="card-body text-center">
                          <div className="avatar avatar-lg bg-warning rounded-circle mb-2 mx-auto">
                            <i className="ti ti-clock text-white fs-20"></i>
                          </div>
                          <h2 className="mb-1"><CountUp end={totalPending} /></h2>
                          <p className="mb-0 text-muted">Pending</p>
                        </div>
                      </div>
                    </div>
                    <div className="col">
                      <div className="card bg-danger-transparent border-0 h-100">
                        <div className="card-body text-center">
                          <div className="avatar avatar-lg bg-danger rounded-circle mb-2 mx-auto">
                            <i className="ti ti-book-off text-white fs-20"></i>
                          </div>
                          <h2 className="mb-1"><CountUp end={totalRejected} /></h2>
                          <p className="mb-0 text-muted">Rejected</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        {charts && <div className="row g-3">
          <div className="col-lg-6 d-flex">
            <div className="card flex-fill">
              <div className="card-header">
                <h4 className="card-title">Enrollment Trends</h4>
              </div>
              <div className="card-body">
                <ReactApexChart
                  options={{
                    chart: { type: "bar", height: 270, toolbar: { show: false } },
                    plotOptions: { bar: { columnWidth: "50%", borderRadius: 4 } },
                    colors: ["#3D5EE1", "#28a745"],
                    dataLabels: { enabled: false },
                    stroke: { show: true, width: 2, colors: ["transparent"] },
                    xaxis: { categories: charts?.enrollment_trends.months ?? [] },
                    legend: { position: "top", horizontalAlign: "left" },
                    tooltip: { y: { formatter: (val: number) => val + " students" } },
                  }}
                  series={(charts?.enrollment_trends.courses ?? []).map(c => ({ name: c.course_name, data: c.data }))}
                  type="bar"
                  height={270}
                />
              </div>
            </div>
          </div>
          <div className="col-lg-6 d-flex">
            <div className="card flex-fill">
              <div className="card-header">
                <h4 className="card-title">Fee Collection Trends</h4>
              </div>
              <div className="card-body">
                <ReactApexChart
                  options={{
                    chart: { type: "area", height: 270, toolbar: { show: false } },
                    colors: ["#28a745", "#E82646"],
                    stroke: { curve: "smooth", width: 2 },
                    fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.7, opacityTo: 0.2, stops: [0, 90, 100] } },
                    xaxis: { categories: charts?.fee_collection_trends.months ?? [] },
                    yaxis: { labels: { formatter: (val: number) => "₹" + (val / 1000) + "k" } },
                    dataLabels: { enabled: false },
                    legend: { position: "top", horizontalAlign: "left" },
                  }}
                  series={[
                    { name: "Collected", data: charts?.fee_collection_trends.collected ?? [] },
                    { name: "Pending", data: charts?.fee_collection_trends.pending ?? [] },
                  ]}
                  type="area"
                  height={270}
                />
              </div>
            </div>
          </div>
          <div className="col-lg-6 d-flex">
            <div className="card flex-fill">
              <div className="card-header">
                <h4 className="card-title">Attendance Trends</h4>
              </div>
              <div className="card-body">
                <ReactApexChart
                  options={{
                    chart: { type: "line", height: 270, toolbar: { show: false } },
                    colors: ["#3D5EE1", "#28a745"],
                    stroke: { curve: "smooth", width: 3 },
                    xaxis: { categories: charts?.attendance_trends.weeks ?? [] },
                    yaxis: { min: 0, max: 100, labels: { formatter: (val: number) => val + "%" } },
                    dataLabels: { enabled: false },
                    legend: { position: "top", horizontalAlign: "left" },
                  }}
                  series={(charts?.attendance_trends.courses ?? []).map(c => ({ name: c.course_name, data: c.data }))}
                  type="line"
                  height={270}
                />
              </div>
            </div>
          </div>
          <div className="col-lg-6 d-flex">
            <div className="card flex-fill">
              <div className="card-header">
                <h4 className="card-title">Level Completion</h4>
              </div>
              <div className="card-body">
                <ReactApexChart
                  options={{
                    chart: { type: "bar", height: 270, toolbar: { show: false } },
                    colors: ["#3D5EE1", "#28a745"],
                    plotOptions: { bar: { columnWidth: "55%", borderRadius: 4 } },
                    dataLabels: { enabled: false },
                    stroke: { width: 2, colors: ["transparent"] },
                    xaxis: { categories: charts?.level_completion.levels ?? [] },
                    yaxis: { min: 0, max: 100, labels: { formatter: (val: number) => val + "%" } },
                    legend: { position: "top", horizontalAlign: "left" },
                    tooltip: { y: { formatter: (val: number) => val + "% completion" } },
                  }}
                  series={(charts?.level_completion.courses ?? []).map(c => ({ name: c.course_name, data: c.data }))}
                  type="bar"
                  height={270}
                />
              </div>
            </div>
          </div>
        </div>}
      </div>
    </div>
  );
};

export default CenterAnalytics;
